import { ComputerAgentDataStatus, MessageRole, ToolCallStatus, ToolName } from "../types";
import { ChatMessage } from "../stores/types";

import {
  parseNavigationFromToolResultContent,
  parseToolCallsFromJson,
  parseToolCallWithNavigation,
} from "./parsers";

import type { ConversationMessage } from "@/queries/vibe/useRecentVibeChatSessions";

const processComputerAgentResult = (
  _toolCallId: string,
  content: string,
  messages: ChatMessage[],
): boolean => {
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].role === MessageRole.COMPUTER_AGENT && messages[i].computerAgent) {
      try {
        const parsed = JSON.parse(content) as Record<string, unknown>;
        const status = (parsed.status as string) ?? ComputerAgentDataStatus.COMPLETED;
        const results =
          typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
            ? ((parsed.results as Record<string, unknown>) ?? parsed)
            : undefined;

        messages[i] = {
          ...messages[i],
          computerAgent: {
            ...messages[i].computerAgent!,
            status:
              status === ComputerAgentDataStatus.CANCELLED
                ? ComputerAgentDataStatus.CANCELLED
                : ComputerAgentDataStatus.COMPLETED,
            ...(results && Object.keys(results).length > 0 ? { results } : {}),
          },
        };
        return true;
      } catch {
        return false;
      }
    }
  }
  return false;
};

const processToolMessage = ({
  toolCallId,
  content,
  messages,
  toolCallMap,
}: {
  toolCallId: string;
  content: string;
  messages: ChatMessage[];
  toolCallMap: Map<string, number>;
}): boolean => {
  if (processComputerAgentResult(toolCallId, content, messages)) {
    return true;
  }

  const toolCallIndex = toolCallMap.get(toolCallId);
  if (toolCallIndex !== undefined && messages[toolCallIndex]?.toolCall) {
    const existing = messages[toolCallIndex].toolCall!;
    const fromResult = parseNavigationFromToolResultContent(content);
    const navigation = fromResult && fromResult.length > 0 ? fromResult : existing.navigation;
    messages[toolCallIndex] = {
      ...messages[toolCallIndex],
      toolCall: {
        ...existing,
        output: content,
        status: ToolCallStatus.COMPLETED,
        completedAt: Date.now(),
        navigation,
      },
    };
    return true;
  }

  const parsedToolCalls = parseToolCallsFromJson(content);
  if (parsedToolCalls) {
    for (const toolCall of parsedToolCalls) {
      const toolName = toolCall.tool_name || "unknown";
      const toolInput = toolCall.tool_input || {};
      const newToolCallId = `tool-${Date.now()}-${Math.random()}`;

      messages.push({
        role: MessageRole.TOOL_CALL,
        content: "",
        toolCall: {
          id: newToolCallId,
          name: toolName,
          input: toolInput,
          status: ToolCallStatus.COMPLETED,
          completedAt: Date.now(),
        },
      });
    }
    return true;
  }
  return false;
};

const processDefaultToolCall = (
  toolCall: {
    id: string;
    type: string;
    function: { name: string; arguments: string };
  },
  messages: ChatMessage[],
  toolCallMap: Map<string, number>,
): void => {
  const parsedToolCall = parseToolCallWithNavigation(toolCall);
  const toolCallIndex = messages.length;
  messages.push({
    role: MessageRole.TOOL_CALL,
    content: "",
    toolCall: parsedToolCall,
  });
  if (parsedToolCall.id) {
    toolCallMap.set(parsedToolCall.id, toolCallIndex);
  }
};

const processAssistantToolCalls = ({
  toolCalls,
  messages,
  toolCallMap,
}: {
  toolCalls: {
    id: string;
    type: string;
    function: {
      name: string;
      arguments: string;
    };
  }[];
  messages: ChatMessage[];
  toolCallMap: Map<string, number>;
}): void => {
  for (const toolCall of toolCalls) {
    switch (toolCall.function.name) {
      case ToolName.COMPUTER_AGENT: {
        messages.push({
          role: MessageRole.COMPUTER_AGENT,
          content: "",
          computerAgent: {
            status: ComputerAgentDataStatus.RUNNING,
          },
        });
        continue;
      }

      default:
        processDefaultToolCall(toolCall, messages, toolCallMap);
        break;
    }
  }
};

const markTrailingCancelledToolCalls = (messages: ChatMessage[]): void => {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m.role === MessageRole.ASSISTANT && (!m.content || m.content.trim() === "")) {
      continue;
    }
    if (
      m.role === MessageRole.COMPUTER_AGENT &&
      m.computerAgent?.status === ComputerAgentDataStatus.RUNNING
    ) {
      messages[i] = {
        ...m,
        computerAgent: { ...m.computerAgent, status: ComputerAgentDataStatus.CANCELLED },
      };
      return;
    }
    if (
      m.role === MessageRole.TOOL_CALL &&
      m.toolCall &&
      (m.toolCall.status === ToolCallStatus.RUNNING || m.toolCall.status === ToolCallStatus.PENDING)
    ) {
      messages[i] = {
        ...m,
        toolCall: {
          ...m.toolCall,
          status: ToolCallStatus.FAILED,
          completedAt: Date.now(),
        },
      };
      return;
    }
    return;
  }
};

export const loadChatHistory = (conversationHistory: ConversationMessage[]): ChatMessage[] => {
  const messages: ChatMessage[] = [];
  const toolCallMap = new Map<string, number>();

  for (const msg of conversationHistory) {
    const getContent = (): string => {
      if (Array.isArray(msg.content)) {
        return msg.content.map(c => c.text).join("");
      }
      if (typeof msg.content === "string") {
        return msg.content;
      }
      return "";
    };
    const content = getContent();

    switch (msg.role) {
      case "tool":
        if (content && msg.tool_call_id) {
          if (
            processToolMessage({
              toolCallId: msg.tool_call_id,
              content,
              messages,
              toolCallMap,
            })
          ) {
            continue;
          }
        }
        break;

      case "assistant": {
        if (content) {
          messages.push({
            role: MessageRole.ASSISTANT,
            content,
          });
        }

        if (msg.tool_calls && msg.tool_calls.length > 0) {
          processAssistantToolCalls({
            toolCalls: msg.tool_calls,
            messages,
            toolCallMap,
          });
        }
        break;
      }

      case "user":
        if (content) {
          messages.push({
            role: MessageRole.USER,
            content,
          });
        }
        break;

      default:
        break;
    }
  }

  markTrailingCancelledToolCalls(messages);

  return messages;
};
